import {useEffect, useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {CircularProgress, Tab, Tabs} from "@mui/material";
import {useQueryClient} from "@tanstack/react-query";
import {zodResolver} from "@hookform/resolvers/zod";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {toast} from "sonner";
import {
  Box,
  Card,
  Colors,
  Grid,
  IconButton,
  IconEdit,
  IconTrash,
  Text,
  Tooltip,
} from "@bounce/shared/ui";
import {useLocalStorageState} from "@bounce/shared/hooks";
import {IconDuplicate} from "../../assets";
import {Button, MenuItem, Select, TextField} from "../../ui/components";
import AreYouSureDialog from "@bounce/shared/consumer/bounce-ui/components/Dialogs/AreYouSureDialog";
import {ReachAccountSearchResult} from "../../apis/requests/accounts/searchAccounts";
import {useCreateTemplate} from "../../apis/requests/templates/createTemplate";
import {useDeleteTemplate} from "../../apis/requests/templates/deleteTemplate";
import {useFetchTemplateMessage} from "../../apis/requests/templates/fetchTemplateMessage";
import {useGetSegmentTags} from "../../apis/requests/templates/getSegmentTags";
import {useGetTemplate} from "../../apis/requests/templates/getTemplate";
import {useTranslateTemplate} from "../../apis/requests/templates/translateTemplate";
import {ReachTemplateContentChannel} from "../../apis/requests/templates/types";
import {useUpdateTemplate} from "../../apis/requests/templates/updateTemplate";
import {useUpdateTranslation} from "../../apis/requests/templates/updateTranslation";
import {TEMPLATE_RENDER_QUERY_KEY} from "../../apis/requests/queryKeys";
import {useReachAuth} from "../../hooks/useReachAuth";
import {reachUrls} from "../../layout/menu-items";
import AutoTranslateDialog from "./spanish/AutoTranslateDialog";
import OfferTranslateDialog from "./spanish/OfferTranslateDialog";
import CollapsibleHtmlField from "./CollapsibleHtmlField";
import DuplicateTemplateDialog from "./DuplicateTemplateDialog";
import GoLiveDialog from "./GoLiveDialog";
import SpanishTranslationCard, {SpanishFields, TranslationStatus} from "./spanish/SpanishTranslationCard";
import {
  BODY_FIELD_CHANNELS,
  CONTENT_CHANNEL_LABELS,
  CONTENT_CHANNELS,
  DEFAULT_ES_CHANNEL_MESSAGE_CODE,
  EMAIL_DOMAIN_TYPE_LABELS,
  EMAIL_DOMAIN_TYPES,
  FormValues,
  formSchema,
  MESSAGING_CHANNELS,
  NEEDS_BODY,
  NEEDS_IMAGE_HTML,
  NEEDS_LANDING_PAGE_SOURCE,
  SHOWS_CHAR_COUNTER,
  SMS_SEGMENT_LEN,
  STATUS_LABELS,
  STATUSES,
  TEMPLATE_TYPE_LABELS,
  TEMPLATE_TYPES,
  UNSUPPORTED_CHANNELS,
} from "./templateFormSchema";
import TemplatePreviewPanel from "./template-preview/TemplatePreviewPanel";
import TemplateResourcesPanel from "./template-preview/TemplateResourcesPanel";
import {TemplateInUseAction, showTemplatesInUse} from "./TemplateInUseDialog";
import {TEMPLATE_SLOT_CHANNELS} from "../triggers/triggerDisplay";
import TemplateTriggeredByPanel from "./TemplateTriggeredByPanel";
import SegmentTagsSelect from "./SegmentTagsSelect";
import {segmentIdsMatchingTemplateType} from "./segmentTagSelection";
import TemplateSourceSyncRow from "./TemplateSourceSyncRow";
import TestSendButton from "./TestSendButton";

export const LAST_PREVIEW_ACCOUNT_KEY = "reach-template-preview-last-account";

const TRANSLATION_POLL_INTERVAL_MS = 5000;
const TAB_QUERY_PARAM = "tab";
const SPANISH_TAB_QUERY_VALUE = "spanish";
const SPANISH_TAB_INDEX = 1;
const BYPASS_REDIRECT_QUERY_PARAM = "bypass";

const TemplateFormPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {id: templateId} = useParams<{id?: string}>();
  const [searchParams] = useSearchParams();
  const isEdit = templateId !== undefined;
  const duplicateFromId = !isEdit ? searchParams.get("duplicate_from") ?? undefined : undefined;
  const isDuplicateMode = duplicateFromId !== undefined;
  const duplicateChannel = searchParams.get("content_channel") as ReachTemplateContentChannel | null;
  const sourceTemplateId = templateId ?? duplicateFromId;

  const defaultSegmentIds = useMemo(
    () =>
      searchParams
        .get("segment_ids")
        ?.split(",")
        .map(Number)
        .filter((n) => Number.isInteger(n) && n > 0) ?? [],
    [searchParams]
  );

  const {data: segments = []} = useGetSegmentTags();
  const [isDeleted, setIsDeleted] = useState(false);
  const {
    data: sourceTemplate,
    isLoading: isLoadingExisting,
    error: loadError,
  } = useGetTemplate(
    {id: sourceTemplateId!},
    {
      enabled: sourceTemplateId !== undefined && !isDeleted,
      refetchOnMount: "always",
      refetchInterval: (data) =>
        data?.extra_info?.translation_status === "translating" ? TRANSLATION_POLL_INTERVAL_MS : false,
    }
  );
  const existing = isEdit ? sourceTemplate : undefined;
  const {mutateAsync: createAsync, isPending: isCreating} = useCreateTemplate();
  const {mutateAsync: updateAsync, isPending: isUpdating} = useUpdateTemplate();
  const {mutateAsync: deleteAsync, isPending: isDeleting} = useDeleteTemplate();
  const {mutateAsync: translateAsync, isPending: isTranslatingTemplate} = useTranslateTemplate();
  const {mutateAsync: updateTranslationAsync, isPending: isUpdatingTranslation} = useUpdateTranslation();
  const isSaving = isCreating || isUpdating || isTranslatingTemplate || isUpdatingTranslation;
  const isBusy = isSaving || isDeleting;

  const {agentEmail} = useReachAuth();

  const [hasSynced, setHasSynced] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [showLandingPageHtml, setShowLandingPageHtml] = useState(false);
  const [showImageHtml, setShowImageHtml] = useState(false);
  const [showBodyHtml, setShowBodyHtml] = useState((existing?.source ?? "cio_studio") === "manual");
  const [lastPreviewAccount, setLastPreviewAccount] = useLocalStorageState<ReachAccountSearchResult | null>(
    LAST_PREVIEW_ACCOUNT_KEY,
    null,
    {encrypted: false}
  );
  const [previewAccount, setPreviewAccountState] = useState<ReachAccountSearchResult | null>(lastPreviewAccount);
  const setPreviewAccount = (account: ReachAccountSearchResult | null) => {
    setPreviewAccountState(account);
    if (account) setLastPreviewAccount(account);
  };
  const existingTranslation = existing?.translations?.[0];
  const effectiveTranslationId = existingTranslation?.id;
  const hasExistingTranslation = !!(existingTranslation?.subject || existingTranslation?.body);
  const [activeTab, setActiveTab] = useState(
    searchParams.get(TAB_QUERY_PARAM) === SPANISH_TAB_QUERY_VALUE ? SPANISH_TAB_INDEX : 0
  );

  useEffect(() => {
    if (searchParams.get(TAB_QUERY_PARAM) === SPANISH_TAB_QUERY_VALUE) setActiveTab(SPANISH_TAB_INDEX);
  }, [searchParams]);

  const [translationError, setTranslationError] = useState<string | null>(null);

  const defaultEsChannelMessageCode = MESSAGING_CHANNELS.has(existing?.content_channel ?? "")
    ? DEFAULT_ES_CHANNEL_MESSAGE_CODE
    : "";

  const spanishMethods = useForm<SpanishFields>({
    values: {
      subject: existingTranslation?.subject ?? "",
      pre_header: existingTranslation?.pre_header ?? "",
      body: existingTranslation?.body ?? "",
      landing_page_body: existingTranslation?.landing_page_body ?? "",
      channel_message_code:
        existingTranslation?.channel_message_code ?? defaultEsChannelMessageCode,
    },
  });

  const onSaveSpanish = spanishMethods.handleSubmit(async (values) => {
    if (!effectiveTranslationId) return;
    try {
      await updateTranslationAsync({
        id: String(effectiveTranslationId),
        payload: {
          subject: values.subject || null,
          pre_header: values.pre_header || null,
          body: values.body,
          landing_page_body: values.landing_page_body || null,
          channel_message_code: values.channel_message_code || null,
        },
      });
      spanishMethods.reset(values);
      toast.success("Spanish translation saved.");
    } catch {
      toast.error("Failed to save Spanish translation.");
    }
  });

  const onTranslate = async () => {
    if (!isEdit || templateId === undefined) return;
    setTranslationError(null);
    try {
      await translateAsync({id: templateId});
    } catch {
      setTranslationError("Something went wrong while translating. Please try again.");
    }
  };

  const translationStatus: TranslationStatus =
    translationError || existing?.extra_info?.translation_status === "failed"
      ? "error"
      : isTranslatingTemplate || existing?.extra_info?.translation_status === "translating"
        ? "translating"
        : hasExistingTranslation
          ? "translated"
          : "empty";

  const showInUse = (action: TemplateInUseAction, triggers = existing?.triggers ?? []) =>
    !!existing && showTemplatesInUse(action, [{id: existing.id, name: existing.name, triggers}]);

  const triggersBlockedBy = (channel: string, type: string) =>
    (existing?.triggers ?? []).filter(
      (reference) =>
        type !== "transactional" || !TEMPLATE_SLOT_CHANNELS[reference.slot].some((allowed) => allowed === channel)
    );

  const onDelete = () => {
    if (!isEdit || templateId === undefined) return;
    if (showInUse("delete")) return;
    NiceModal.show(AreYouSureDialog, {
      onConfirm: async () => {
        setIsDeleted(true);
        try {
          await deleteAsync({id: templateId});
        } catch {
          setIsDeleted(false);
          return;
        }
        toast.success("Template deleted");
        navigate(reachUrls.templates);
      },
      contentTextId: "Are you sure you want to delete this template?",
      confirmTextId: "Delete",
    });
  };

  const onDuplicate = () => {
    if (!isEdit || templateId === undefined || !existing) return;
    NiceModal.show(DuplicateTemplateDialog, {
      defaultChannel: existing.content_channel,
      hasPendingChanges: isDirty,
      onConfirm: (channel: ReachTemplateContentChannel) => {
        const run = async () => {
          if (isDirty) {
            const isValid = await trigger();
            if (!isValid) return;
            await updateAsync({id: templateId, payload: buildPayload(getValues())});
          }
          navigate(`${reachUrls.templatesNew}?duplicate_from=${templateId}&content_channel=${channel}`);
        };
        run();
      },
    });
  };

  const prefill = sourceTemplate;
  const prefillMmsImage =
    prefill?.content_channel === "mms" ? prefill?.body || prefill?.landing_page_body || "" : null;

  const prefillIsSmsWithLandingPage = prefill?.content_channel === "sms_with_landing_page";
  const targetChannel = (isDuplicateMode ? duplicateChannel : null) ?? prefill?.content_channel ?? "email";
  const duplicatingIntoMessaging =
    isDuplicateMode &&
    !MESSAGING_CHANNELS.has(prefill?.content_channel ?? "") &&
    MESSAGING_CHANNELS.has(targetChannel);
  const prefillHtmlPage = NEEDS_LANDING_PAGE_SOURCE.has(prefill?.content_channel ?? "")
    ? prefill?.landing_page_body
    : prefill?.body;
  const methods = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      name: prefill?.name ?? "",
      template_key: prefill?.template_key ?? "",
      segment_ids: prefill?.segments.map((s) => s.id) ?? defaultSegmentIds,
      status: isDuplicateMode ? "draft" : prefill?.status ?? "draft",
      type: prefill?.type ?? "marketing",
      content_channel: (isDuplicateMode ? duplicateChannel : null) ?? prefill?.content_channel ?? "email",
      source: (prefillIsSmsWithLandingPage ? prefill?.landing_page_source : prefill?.source) ?? "cio_regular",
      source_link: (prefillIsSmsWithLandingPage ? prefill?.landing_page_source_link : prefill?.source_link) ?? "",
      pre_header: prefill?.pre_header ?? "",
      channel_message_code: prefill?.channel_message_code ?? null,
      weight: prefill?.weight ?? 1,
      subject: duplicatingIntoMessaging ? "" : prefill?.subject ?? "",
      body: prefillMmsImage ?? (targetChannel === "email" ? prefillHtmlPage : prefill?.body) ?? "",
      landing_page_source: prefill?.landing_page_source ?? "cio_regular",
      landing_page_source_link: prefill?.landing_page_source_link ?? "",
      landing_page_body: (NEEDS_LANDING_PAGE_SOURCE.has(targetChannel) ? prefillHtmlPage : prefill?.landing_page_body) ?? "",
      email_domain_type: prefill?.email_domain_type ?? null,
    },
  });
  const {
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    setValue,
    reset,
    formState: {isDirty, errors},
  } = methods;

  const onCancel = () => {
    reset();
    spanishMethods.reset();
  };

  const source = watch("source");
  const contentChannel = watch("content_channel");
  const templateType = watch("type");
  const isTransactional = templateType === "transactional";
  const isValidation = templateType === "validation";
  const channelMessageCode = watch("channel_message_code");
  const isCioSource = source === "cio_studio" || source === "cio_regular";
  const showDetailsCard = isEdit || isDuplicateMode || source === "manual" || hasSynced;
  const subject = watch("subject") ?? "";
  const body = watch("body") ?? "";
  const isMessagingChannel = MESSAGING_CHANNELS.has(contentChannel);
  const showsCharCounter = SHOWS_CHAR_COUNTER.has(contentChannel);
  const needsImageHtml = NEEDS_IMAGE_HTML.has(contentChannel);
  const needsLandingPageSource = NEEDS_LANDING_PAGE_SOURCE.has(contentChannel);
  const subjectLabel = isMessagingChannel ? "Message" : "Subject";
  const showsPreHeader = contentChannel === "email";
  const showsEmailDomainType = contentChannel === "email";
  const showsBody = NEEDS_BODY.has(contentChannel);
  const counterValue = isMessagingChannel ? subject : body;

  const bypassSpanishRedirect = searchParams.get(BYPASS_REDIRECT_QUERY_PARAM) === "true";
  const englishSourceId =
    isEdit && !bypassSpanishRedirect && existing?.language === "es" ? existing.source_template_id : null;

  useEffect(() => {
    if (englishSourceId === null) return;
    const englishPath = reachUrls.templateById.replace(":id", String(englishSourceId));
    navigate(`${englishPath}?${TAB_QUERY_PARAM}=${SPANISH_TAB_QUERY_VALUE}`, {replace: true});
  }, [englishSourceId]);

  useEffect(() => {
    if (isTransactional && getValues("segment_ids").length > 0) {
      setValue("segment_ids", [], {shouldDirty: true});
    }
  }, [isTransactional]);

  useEffect(() => {
    const segmentIds = getValues("segment_ids");
    const allowedSegmentIds = segmentIdsMatchingTemplateType(segmentIds, segments, isValidation);
    if (allowedSegmentIds.length !== segmentIds.length) {
      setValue("segment_ids", allowedSegmentIds, {shouldDirty: true});
    }
  }, [isValidation, segments]);

  const {mutate: fetchTemplateMessage, isPending: isFetchingTemplateMessage} = useFetchTemplateMessage();
  const handleFetchTemplateMessage = () => {
    if (!channelMessageCode) return;
    fetchTemplateMessage(
      {code: channelMessageCode},
      {
        onSuccess: ({template_message}) => {
          if (!template_message) {
            toast.error("No message found for this code");
            return;
          }
          setValue("subject", template_message, {shouldDirty: true});
        },
      }
    );
  };

  useEffect(() => {
    if (!duplicatingIntoMessaging || !channelMessageCode) return;
    handleFetchTemplateMessage();
  }, [duplicatingIntoMessaging, channelMessageCode]);

  const buildPayload = (values: FormValues) => {
    const isEmailChannel = values.content_channel === "email";
    const needsLandingPageSource = NEEDS_LANDING_PAGE_SOURCE.has(values.content_channel);
    const isSmsWithLandingPage = values.content_channel === "sms_with_landing_page";
    const isMmsWithLandingPage = values.content_channel === "mms_with_landing_page";
    return {
      ...values,
      source: isSmsWithLandingPage ? null : values.source,
      source_link: isSmsWithLandingPage ? null : values.source_link,
      landing_page_source: isSmsWithLandingPage
        ? values.source
        : isMmsWithLandingPage
          ? values.landing_page_source
          : null,
      landing_page_source_link: isSmsWithLandingPage
        ? values.source_link
        : isMmsWithLandingPage
          ? values.landing_page_source_link
          : null,
      landing_page_body: needsLandingPageSource ? values.landing_page_body : null,
      subject: values.subject || null,
      pre_header: isEmailChannel ? values.pre_header || null : null,
      email_domain_type: isEmailChannel ? values.email_domain_type : null,
      body: BODY_FIELD_CHANNELS.has(values.content_channel) ? values.body : "",
      segment_ids: values.type === "transactional" ? [] : values.segment_ids,
    };
  };

  const performSave = async (values: FormValues, autoTranslate: boolean) => {
    const payload = buildPayload(values);
    if (isEdit && templateId !== undefined) {
      await updateAsync({id: templateId, payload: {...payload, auto_translate: autoTranslate}});
      toast.success("Template updated");
      if (previewAccount) {
        await queryClient.refetchQueries({
          queryKey: [TEMPLATE_RENDER_QUERY_KEY, String(previewAccount.account_id), String(templateId)],
        });
      }
    } else {
      const created = await createAsync(payload);
      toast.success("Template created");
      if (created.language === "en") {
        NiceModal.show(OfferTranslateDialog, {
          onConfirm: (translate: boolean) => {
            if (translate) {
              translateAsync({id: String(created.id)}).catch(() => toast.error("Translation failed to start."));
            }
            navigate(`/templates/${created.id}`, {replace: true});
          },
        });
        return;
      }
      navigate(`/templates/${created.id}`, {replace: true});
      return;
    }

    const spanish = spanishMethods.getValues();
    if (spanishMethods.formState.isDirty && effectiveTranslationId && !autoTranslate) {
      await updateTranslationAsync({
        id: String(effectiveTranslationId),
        payload: {
          subject: spanish.subject || null,
          pre_header: spanish.pre_header || null,
          body: spanish.body,
          landing_page_body: spanish.landing_page_body || null,
          channel_message_code: spanish.channel_message_code || null,
        },
      });
      spanishMethods.reset(spanish);
    }
  };

  const hasTranslatableContentChanged = (values: FormValues) =>
    !existing ||
    values.subject !== (existing.subject ?? "") ||
    values.pre_header !== (existing.pre_header ?? "") ||
    values.body !== (existing.body ?? "") ||
    values.landing_page_body !== (existing.landing_page_body ?? "");

  const proceedSave = async (values: FormValues) => {
    if (isEdit && effectiveTranslationId && hasTranslatableContentChanged(values)) {
      NiceModal.show(AutoTranslateDialog, {
        onConfirm: (autoTranslate: boolean) => performSave(values, autoTranslate),
      });
      return;
    }
    await performSave(values, false);
  };

  const onSubmit = handleSubmit(async (values) => {
    if (values.status === "draft") {
      NiceModal.show(GoLiveDialog, {
        onConfirm: (goLive: boolean) => {
          if (goLive) setValue("status", "active", {shouldDirty: true});
          proceedSave(goLive ? {...values, status: "active"} : values);
        },
      });
      return;
    }
    await proceedSave(values);
  });

  if (sourceTemplateId !== undefined && (isLoadingExisting || englishSourceId !== null)) {
    return (
      <Box sx={{p: 4}}>
        <Text size={14}>Loading…</Text>
      </Box>
    );
  }

  if (sourceTemplateId !== undefined && loadError) {
    return (
      <Box sx={{p: 4}}>
        <Text size={14}>Failed to load template.</Text>
      </Box>
    );
  }

  return (
    <Box sx={{p: 4}}>
      <FormProvider {...methods}>
        <Box>
          <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
              <IconButton
                onClick={() => navigate(reachUrls.templates)}
                sx={{
                  border: `1px solid ${Colors.tertiary._300}`,
                  borderRadius: "8px",
                  padding: "6px",
                  color: Colors.grey._700,
                  "&:hover": {backgroundColor: Colors.tertiary._100},
                }}
              >
                <ArrowBackIcon sx={{fontSize: 20}} />
              </IconButton>
              <Text size={24} variant="interDisplay600">
                {isEdit ? "Edit template" : isDuplicateMode ? "Duplicate template" : "New template"}
              </Text>
            </Box>
            <Box sx={{display: "flex", gap: 1, alignItems: "center"}}>
              {isEdit && (
                <>
                  <Tooltip title="Duplicate template">
                    <IconButton
                      onClick={onDuplicate}
                      disabled={isBusy}
                      sx={{
                        border: `1px solid ${Colors.tertiary._300}`,
                        borderRadius: "8px",
                        padding: "6px",
                        color: Colors.grey._700,
                        "& svg path, & svg rect": {stroke: "currentColor"},
                        "&:hover": {backgroundColor: Colors.tertiary._100},
                      }}
                    >
                      <Box component={IconDuplicate} sx={{width: 20, height: 20}} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete template">
                    <IconButton
                      onClick={onDelete}
                      disabled={isBusy}
                      sx={{
                        border: `1px solid ${Colors.red._300}`,
                        borderRadius: "8px",
                        padding: "6px",
                        color: Colors.red._600,
                        "& svg path": {stroke: "currentColor"},
                        "&:hover": {backgroundColor: Colors.red._100, color: Colors.red._800},
                      }}
                    >
                      <Box component={IconTrash} sx={{width: 20, height: 20}} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <TestSendButton
                disabled={isBusy}
                templateId={templateId}
                previewAccount={previewAccount}
                agentEmail={agentEmail}
                existingTranslation={existingTranslation}
              />
              {isEdit && (
                <Button buttonType="outlined-primary" size="small" onClick={onCancel} disabled={isBusy || !isDirty}>
                  Cancel
                </Button>
              )}
              <Button
                buttonType="contained-primary"
                size="small"
                onClick={onSubmit}
                disabled={isBusy || (!isDirty && !isDuplicateMode)}
              >
                {isEdit ? "Save changes" : "Save template"}
              </Button>
            </Box>
          </Box>

          <Box sx={{display: "flex", gap: 2, alignItems: "flex-start"}}>
            <Box sx={{flex: "1 1 0", minWidth: 0, maxWidth: 760, display: "flex", flexDirection: "column", gap: 3}}>
              <Card
                fullWidth
                sx={{
                  alignItems: "start",
                  gap: "24px",
                  padding: "24px",
                  borderRadius: "16px",
                }}
              >
                <Controller
                  name="name"
                  control={control}
                  render={({field: {ref, ...field}, fieldState}) =>
                    isEditingName ? (
                      <TextField
                        {...field}
                        inputRef={ref}
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Untitled template"
                        error={!!fieldState.error}
                        onBlur={() => {
                          field.onBlur();
                          setIsEditingName(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            setIsEditingName(false);
                          }
                        }}
                      />
                    ) : (
                      <Box
                        onClick={() => setIsEditingName(true)}
                        sx={{display: "flex", alignItems: "center", gap: 1, cursor: "pointer"}}
                      >
                        <Text
                          variant="interDisplay600"
                          size={16}
                          sx={{color: Colors.grey._700}}
                        >
                          {field.value || "Untitled template"}
                        </Text>
                        <Box
                          component={IconEdit}
                          sx={{width: 20, height: 20, color: Colors.grey._400, flexShrink: 0}}
                        />
                        {fieldState.error && (
                          <Text size={12} variant="inter600" sx={{color: Colors.red._900}}>
                            Required
                          </Text>
                        )}
                      </Box>
                    )
                  }
                />

                <Grid container spacing={2.5} sx={{width: "100%"}}>
                  <Grid size={4}>
                    <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
                      Content channel *
                    </Text>
                    <Controller
                      name="content_channel"
                      control={control}
                      render={({field}) => (
                        <Select
                          {...field}
                          fullWidth
                          size="small"
                          displayEmpty
                          onChange={(event) => {
                            const next = String(event.target.value);
                            if (showInUse("channel", triggersBlockedBy(next, templateType))) return;
                            field.onChange(event);
                          }}
                        >
                          {CONTENT_CHANNELS.map((ct) => (
                            <MenuItem key={ct} value={ct} disabled={UNSUPPORTED_CHANNELS.has(ct)}>
                              {CONTENT_CHANNEL_LABELS[ct]}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </Grid>

                  <Grid size={4}>
                    <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
                      Type *
                    </Text>
                    <Controller
                      name="type"
                      control={control}
                      render={({field}) => (
                        <Select
                          {...field}
                          fullWidth
                          size="small"
                          displayEmpty
                          onChange={(event) => {
                            const next = String(event.target.value);
                            if (showInUse("type", triggersBlockedBy(contentChannel, next))) return;
                            field.onChange(event);
                          }}
                        >
                          {TEMPLATE_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                              {TEMPLATE_TYPE_LABELS[t]}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </Grid>

                  <Grid size={4}>
                    <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
                      Status *
                    </Text>
                    <Controller
                      name="status"
                      control={control}
                      render={({field}) => (
                        <Select
                          {...field}
                          fullWidth
                          size="small"
                          displayEmpty
                          onChange={(event) => {
                            const next = event.target.value;
                            if (next === "inactive" && showInUse("deactivate")) return;
                            if (next === "draft" && existing?.status === "active" && showInUse("draft")) return;
                            field.onChange(event);
                          }}
                        >
                          {STATUSES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </Grid>

                  {!!existing?.triggers?.length && (
                    <Grid size={12}>
                      <TemplateTriggeredByPanel triggers={existing.triggers} />
                    </Grid>
                  )}

                  {!isTransactional && (
                    <Grid size={12}>
                      <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
                        Segment tags
                      </Text>
                      <Controller
                        name="segment_ids"
                        control={control}
                        render={({field}) => (
                          <SegmentTagsSelect
                            segments={segments}
                            value={field.value}
                            onChange={field.onChange}
                            validationOnly={isValidation}
                          />
                        )}
                      />
                    </Grid>
                  )}

                  <Grid size={12}>
                    <TemplateSourceSyncRow
                      label={
                        contentChannel === "mms_with_landing_page"
                          ? "Media source *"
                          : contentChannel === "sms_with_landing_page"
                            ? "Landing page source *"
                            : undefined
                      }
                      onSyncResult={(result) => {
                        setHasSynced(true);
                        if (contentChannel !== "email") {
                          setValue("subject", result.message ?? "", {shouldDirty: true});
                        }
                      }}
                    />
                  </Grid>

                  {contentChannel === "mms_with_landing_page" && (
                    <Grid size={12}>
                      <TemplateSourceSyncRow variant="landing_page" onSyncResult={() => setHasSynced(true)} />
                    </Grid>
                  )}

                  <Grid size={showsEmailDomainType ? 6 : 12}>
                    <Box sx={{display: "flex", alignItems: "baseline", gap: 0.5, mb: 0.5}}>
                      <Text size={13} sx={{color: Colors.grey._700}}>
                        Template key {!isCioSource && "*"}
                      </Text>
                      {isCioSource && (
                        <Text size={11} sx={{color: Colors.grey._400, fontStyle: "italic"}}>
                          from sync
                        </Text>
                      )}
                      {!isCioSource && errors.template_key && (
                        <Text size={12} sx={{color: Colors.red._900}}>
                          Required
                        </Text>
                      )}
                    </Box>
                    <Controller
                      name="template_key"
                      control={control}
                      render={({field: {ref, ...field}, fieldState}) =>
                        isCioSource ? (
                          <TextField
                            value={field.value}
                            fullWidth
                            size="small"
                            slotProps={{htmlInput: {readOnly: true}}}
                            placeholder={field.value ? undefined : "Populated after sync"}
                            InputSx={{
                              fontFamily: field.value ? "monospace" : "inherit",
                              backgroundColor: Colors.tertiary._100,
                              "& .MuiOutlinedInput-notchedOutline": field.value
                                ? {borderColor: Colors.tertiary._200}
                                : {borderStyle: "dashed", borderColor: Colors.tertiary._300},
                              "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: Colors.tertiary._300},
                              "& input": {color: Colors.grey._700},
                            }}
                          />
                        ) : (
                          <TextField
                            {...field}
                            inputRef={ref}
                            fullWidth
                            size="small"
                            placeholder="Template key"
                            error={!!fieldState.error}
                          />
                        )
                      }
                    />
                  </Grid>

                  {showsEmailDomainType && (
                    <Grid size={6}>
                      <Box sx={{display: "flex", alignItems: "baseline", gap: 0.5, mb: 0.5}}>
                        <Text size={13} sx={{color: Colors.grey._700}}>
                          Email domain type
                        </Text>
                        {isCioSource && (
                          <Text size={11} sx={{color: Colors.grey._400, fontStyle: "italic"}}>
                            from sync
                          </Text>
                        )}
                      </Box>
                      {isCioSource && !watch("email_domain_type") ? (
                        <TextField
                          value=""
                          fullWidth
                          size="small"
                          slotProps={{htmlInput: {readOnly: true}}}
                          placeholder="Populated after sync"
                          InputSx={{
                            backgroundColor: Colors.tertiary._100,
                            "& .MuiOutlinedInput-notchedOutline": {borderStyle: "dashed", borderColor: Colors.tertiary._300},
                          }}
                        />
                      ) : (
                        <Controller
                          name="email_domain_type"
                          control={control}
                          render={({field}) => (
                            <Select
                              {...field}
                              value={field.value ?? ""}
                              onChange={(e) => field.onChange(e.target.value || null)}
                              fullWidth
                              size="small"
                              displayEmpty
                            >
                              {EMAIL_DOMAIN_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {EMAIL_DOMAIN_TYPE_LABELS[t]}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        />
                      )}
                    </Grid>
                  )}
                </Grid>
              </Card>

              {showDetailsCard && (
                <Card
                  fullWidth
                  sx={{
                    alignItems: "start",
                    gap: "24px",
                    padding: "24px",
                    borderRadius: "16px",
                  }}
                >
                  <Text variant="interDisplay600" size={16} sx={{color: Colors.grey._700}}>
                    Content
                  </Text>
                  {(!isEdit || existing?.language !== "es") && (
                    <Tabs
                      value={activeTab}
                      onChange={(_, v) => setActiveTab(v)}
                      sx={{
                        minHeight: 36,
                        mb: "-12px",
                        "& .MuiTabs-indicator": {backgroundColor: Colors.blue._600},
                        "& .MuiTab-root": {
                          textTransform: "none",
                          minHeight: 36,
                          py: 0.5,
                          px: 2,
                          fontSize: 14,
                          color: Colors.grey._900,
                          "&.Mui-selected": {color: Colors.blue._600},
                        },
                      }}
                    >
                      <Tab label="English" />
                      <Tab
                        label={
                          <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                            Spanish
                            {existing?.extra_info?.translation_status === "translating" && (
                              <CircularProgress size={14} sx={{color: Colors.blue._600}} />
                            )}
                            {existing?.extra_info?.translation_status === "success" && (
                              <CheckCircleIcon sx={{fontSize: 14, color: Colors.green._600}} />
                            )}
                            {existing?.extra_info?.translation_status === "failed" && (
                              <CancelIcon sx={{fontSize: 14, color: Colors.red._600}} />
                            )}
                          </Box>
                        }
                      />
                    </Tabs>
                  )}

                  {activeTab === 1 && (!isEdit || existing?.language !== "es") ? (
                    <FormProvider {...spanishMethods}>
                      <SpanishTranslationCard
                        onTranslate={onTranslate}
                        onSave={onSaveSpanish}
                        isTranslating={isTranslatingTemplate}
                        isSaving={isUpdatingTranslation}
                        showPreHeader={showsPreHeader}
                        showBody={showsBody}
                        showLandingPageBody={needsLandingPageSource}
                        showChannelMessageCode={isMessagingChannel}
                        translationStatus={translationStatus}
                        translationError={translationError ?? undefined}
                        isNewTemplate={!isEdit}
                      />
                    </FormProvider>
                  ) : !isCioSource ? (
                    <Grid container spacing={2.5} sx={{width: "100%"}}>
                      {isMessagingChannel && (
                        <Grid size={12}>
                          <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
                            Channel message code
                          </Text>
                          <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <Controller
                              name="channel_message_code"
                              control={control}
                              render={({field: {ref, ...field}}) => (
                                <TextField
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  inputRef={ref}
                                  fullWidth
                                  size="small"
                                  placeholder="Message code"
                                />
                              )}
                            />
                            <Button
                              buttonType="outlined-primary"
                              size="small"
                              onClick={handleFetchTemplateMessage}
                              disabled={!channelMessageCode || isFetchingTemplateMessage}
                            >
                              Fetch
                            </Button>
                          </Box>
                        </Grid>
                      )}

                      <Grid size={12}>
                        <Box sx={{display: "flex", alignItems: "baseline", gap: 1, mb: 0.5}}>
                          <Text size={13} sx={{color: Colors.grey._700}}>
                            {subjectLabel}
                          </Text>
                          {errors.subject && (
                            <Text size={12} sx={{color: Colors.red._900}}>
                              {errors.subject.message ?? "Required"}
                            </Text>
                          )}
                        </Box>
                        <Controller
                          name="subject"
                          control={control}
                          render={({field: {ref, ...field}, fieldState}) => (
                            <TextField
                              {...field}
                              inputRef={ref}
                              fullWidth
                              size="small"
                              multiline
                              minRows={isMessagingChannel ? 4 : 1}
                              placeholder={isMessagingChannel ? "Hi {{first_name}}, ..." : "Email subject line"}
                              error={!!fieldState.error}
                              disabled={isMessagingChannel}
                            />
                          )}
                        />
                        {showsCharCounter && (
                          <Text
                            size={12}
                            sx={{
                              mt: 0.5,
                              display: "block",
                              color: counterValue.length > SMS_SEGMENT_LEN ? Colors.red._800 : Colors.grey._500,
                            }}
                          >
                            {`${counterValue.length} / ${SMS_SEGMENT_LEN} chars · ${Math.max(
                              1,
                              Math.ceil(counterValue.length / SMS_SEGMENT_LEN)
                            )} segments`}
                          </Text>
                        )}
                      </Grid>

                      {showsPreHeader && (
                        <Grid size={12}>
                          <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
                            Pre-header
                          </Text>
                          <Controller
                            name="pre_header"
                            control={control}
                            render={({field: {ref, ...field}}) => (
                              <TextField
                                {...field}
                                inputRef={ref}
                                fullWidth
                                size="small"
                                placeholder="Email pre-header line"
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {showsBody && (
                        <Grid size={12}>
                          <CollapsibleHtmlField
                            name="body"
                            label="Body / HTML *"
                            show={showBodyHtml}
                            onToggle={setShowBodyHtml}
                            minRows={6}
                            placeholder="<html>...</html>"
                            error={!!errors.body}
                          />
                        </Grid>
                      )}

                      {needsImageHtml && (
                        <Grid size={12}>
                          <CollapsibleHtmlField
                            name="body"
                            label="Image HTML"
                            show={showImageHtml}
                            onToggle={setShowImageHtml}
                            minRows={4}
                            placeholder='<img src="..." />'
                          />
                        </Grid>
                      )}

                      {needsLandingPageSource && (
                        <Grid size={12}>
                          <CollapsibleHtmlField
                            name="landing_page_body"
                            label="Landing page HTML"
                            show={showLandingPageHtml}
                            onToggle={setShowLandingPageHtml}
                            minRows={6}
                            placeholder="<html>...</html>"
                          />
                        </Grid>
                      )}
                    </Grid>
                  ) : (
                    <Grid container spacing={2.5} sx={{width: "100%"}}>
                      {isMessagingChannel && (
                        <Grid size={12}>
                          <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
                            Channel message code
                          </Text>
                          <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                            <Controller
                              name="channel_message_code"
                              control={control}
                              render={({field: {ref, ...field}}) => (
                                <TextField
                                  {...field}
                                  value={field.value ?? ""}
                                  onChange={(e) => field.onChange(e.target.value || null)}
                                  inputRef={ref}
                                  fullWidth
                                  size="small"
                                  placeholder="Message code"
                                />
                              )}
                            />
                            <Button
                              buttonType="outlined-primary"
                              size="small"
                              onClick={handleFetchTemplateMessage}
                              disabled={!channelMessageCode || isFetchingTemplateMessage}
                            >
                              Fetch
                            </Button>
                          </Box>
                        </Grid>
                      )}

                      <Grid size={12}>
                        <Box sx={{display: "flex", alignItems: "baseline", gap: 1, mb: 0.5}}>
                          <Text size={13} sx={{color: Colors.grey._700}}>
                            {subjectLabel}
                          </Text>
                          {errors.subject && (
                            <Text size={12} sx={{color: Colors.red._900}}>
                              {errors.subject.message ?? "Required"}
                            </Text>
                          )}
                        </Box>
                        <Controller
                          name="subject"
                          control={control}
                          render={({field: {ref, ...field}, fieldState}) => (
                            <TextField
                              {...field}
                              inputRef={ref}
                              fullWidth
                              size="small"
                              multiline
                              minRows={isMessagingChannel ? 4 : 1}
                              placeholder={isMessagingChannel ? "Hi {{first_name}}, ..." : "Email subject line"}
                              error={!!fieldState.error}
                              disabled={isMessagingChannel}
                            />
                          )}
                        />
                        {showsCharCounter && (
                          <Text
                            size={12}
                            sx={{
                              mt: 0.5,
                              display: "block",
                              color: counterValue.length > SMS_SEGMENT_LEN ? Colors.red._800 : Colors.grey._500,
                            }}
                          >
                            {`${counterValue.length} / ${SMS_SEGMENT_LEN} chars · ${Math.max(
                              1,
                              Math.ceil(counterValue.length / SMS_SEGMENT_LEN)
                            )} segments`}
                          </Text>
                        )}
                      </Grid>

                      {showsPreHeader && (
                        <Grid size={12}>
                          <Text size={13} sx={{color: Colors.grey._700, mb: 0.5}}>
                            Pre-header
                          </Text>
                          <Controller
                            name="pre_header"
                            control={control}
                            render={({field: {ref, ...field}}) => (
                              <TextField
                                {...field}
                                inputRef={ref}
                                fullWidth
                                size="small"
                                placeholder="Email pre-header line"
                              />
                            )}
                          />
                        </Grid>
                      )}

                      {showsBody && (
                        <Grid size={12}>
                          <CollapsibleHtmlField
                            name="body"
                            label="Body / HTML"
                            show={showBodyHtml}
                            onToggle={setShowBodyHtml}
                            minRows={6}
                            placeholder="<html>...</html>"
                          />
                        </Grid>
                      )}

                      {needsImageHtml && (
                        <Grid size={12}>
                          <CollapsibleHtmlField
                            name="body"
                            label="Image HTML"
                            show={showImageHtml}
                            onToggle={setShowImageHtml}
                            minRows={4}
                            placeholder='<img src="..." />'
                          />
                        </Grid>
                      )}

                      {needsLandingPageSource && (
                        <Grid size={12}>
                          <CollapsibleHtmlField
                            name="landing_page_body"
                            label="Landing page HTML"
                            show={showLandingPageHtml}
                            onToggle={setShowLandingPageHtml}
                            minRows={6}
                            placeholder="<html>...</html>"
                          />
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Card>
              )}
            </Box>

            <Box sx={{flex: "1 1 0", minWidth: 0, position: "sticky", top: 16}}>
              <TemplateResourcesPanel
                snippets={existing?.snippets}
                assets={existing?.assets}
                unknownSnippets={existing?.unknown_snippets}
              />
              <TemplatePreviewPanel
                canPreviewUnsaved={hasSynced || isDuplicateMode}
                selectedAccount={previewAccount}
                onSelectAccount={setPreviewAccount}
                translationId={effectiveTranslationId}
              />
            </Box>
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default TemplateFormPage;
